export type ResolvedListingProvider = {
  key: string
  label: string
  logoUrl?: string
  href?: string
}

/**
 * Marketplace board chrome registry. Add one entry when a pack enables a new
 * `opportunityIngest.providers` key (Jooble shipped; CareerJet when TC-422 lands).
 */
const MARKETPLACE_PROVIDER_REGISTRY: Record<
  string,
  Pick<ResolvedListingProvider, 'label' | 'logoUrl' | 'href'>
> = {
  adzuna: {
    label: 'Jobs by Adzuna',
    href: 'https://www.adzuna.com.au/',
  },
  jooble: {
    label: 'Jobs by Jooble',
    href: 'https://au.jooble.org/',
  },
  // careerjet: { label: 'Jobs by CareerJet', href: '…' } — enable with pack provider + ingest
}

/** Optional block-variant compliance / apply hints keyed by provider. */
const MARKETPLACE_PROVIDER_DETAIL_HINTS: Record<string, string> = {
  adzuna: 'Listings provided via Adzuna search API. Apply on the employer site via Adzuna.',
  jooble: 'Listings provided via Jooble. Apply on the employer site via Jooble.',
}

export type ResolveListingProviderInput = {
  kind?: string | null
  provider?: string | null
}

/**
 * Resolve external listing source for job surfaces (marketplace ingest partners).
 * Managed / tenant-native postings return null — no provider chrome on internal jobs.
 */
export function resolveListingProvider({
  kind,
  provider,
}: ResolveListingProviderInput): ResolvedListingProvider | null {
  const normalizedKind = (kind || 'managed').trim().toLowerCase()
  const normalizedProvider = (provider || '').trim().toLowerCase()

  if (normalizedKind !== 'marketplace') {
    return null
  }

  const key = normalizedProvider || 'adzuna'
  const registry = MARKETPLACE_PROVIDER_REGISTRY[key]
  if (registry) {
    return { key, ...registry }
  }
  return {
    key,
    label: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
  }
}

export function resolveListingProviderFromOpportunity(
  opportunity: { kind?: string | null; provider?: string | null },
): ResolvedListingProvider | null {
  return resolveListingProvider({
    kind: opportunity.kind,
    provider: opportunity.provider,
  })
}

/** Block-variant helper copy for marketplace detail (provider compliance). */
export function listingProviderDetailHint(key: string): string | null {
  const normalized = (key || '').trim().toLowerCase()
  return MARKETPLACE_PROVIDER_DETAIL_HINTS[normalized] ?? null
}

/** Title-case provider key for admin chips / stats labels. */
export function formatMarketplaceProviderLabel(key: string): string {
  const normalized = (key || '').trim().toLowerCase()
  const registry = MARKETPLACE_PROVIDER_REGISTRY[normalized]
  if (registry?.label) {
    return registry.label.replace(/^Jobs by\s+/i, '')
  }
  return normalized.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || 'Provider'
}
